import { DownloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, Flex, Input, message, Modal, Table, TableColumnsType, Tag, Typography } from 'antd'
import { PartnerReferralTransactionItem } from 'Modules'
import { useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import LinkAddress from 'component/link/link-address'
import SelectPto from 'component/select/select-pto'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_MAX } from 'constants/pagination'
import useSearchParams from 'hooks/use-search-params'
import { downloadBlob, generateFilename } from 'utils/download-file'
import { logError } from 'utils/logger'

const fetchPartnerTransactions = async (
  walletAddress: string,
  params: { ptoId?: string; fromAddress?: string; page?: number; limit?: number }
) => {
  const response = await AppAdmin.getPartnerReferralTransactions(walletAddress, params)
  const payload = response.data?.data

  return {
    data: (payload?.results ?? []) as PartnerReferralTransactionItem[],
    total: payload?.pagination?.totalResults ?? 0,
  }
}

/**
 * Referral Performance modal for a partner — transactions (PTO purchases) of the
 * users they invited. Same UI as the normal-user Referral Performance modal,
 * plus an Export button.
 */
export const ModalPartnerTransactions = ({
  walletAddress,
  onClose,
}: {
  walletAddress: string
  onClose: VoidFunction
}) => {
  const { searchParams, setSearchParams, initPagination, onChangePagination } = useSearchParams()
  const [ptoId, setPtoId] = useState<string | undefined>()
  const [search, setSearch] = useState<string>('')
  const [exporting, setExporting] = useState(false)

  const { page, limit } = searchParams || {}
  const fromAddress = search.trim() || undefined

  const { data, isLoading } = useQuery({
    queryKey: ['partnerReferralTransactions', walletAddress, ptoId, search, page, limit],
    queryFn: () => fetchPartnerTransactions(walletAddress, { ptoId, fromAddress, page, limit }),
    gcTime: 30000,
  })

  const resetPage = () => {
    setSearchParams((prev) => ({ ...prev, page: DEFAULT_PAGE_INDEX }))
  }

  const handlePtoChange = (value: string | undefined) => {
    setPtoId(value)
    resetPage()
  }

  const handleWalletSearch = (value: string) => {
    setSearch(value.trim())
    resetPage()
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const res = await AppAdmin.exportPartnerReferralTransactions(walletAddress, {
        ptoId,
        fromAddress,
      })
      downloadBlob(res.data, generateFilename('partner_transactions'))
    } catch (error) {
      logError('Export partner transactions failed', error)
      message.error('Failed to export transactions')
    } finally {
      setExporting(false)
    }
  }

  const transactions = data?.data || []

  const columns: TableColumnsType<PartnerReferralTransactionItem> = [
    {
      title: 'No.',
      key: 'no',
      render: (_: any, __: any, index: number) => {
        return (
          (Number(page || DEFAULT_PAGE_INDEX) - 1) * Number(limit || DEFAULT_PAGE_SIZE) + index + 1
        )
      },
      align: 'center',
    },
    {
      title: 'Wallet Address',
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      render: (address: string) => <LinkAddress address={address} />,
    },
    {
      title: 'PTO name',
      dataIndex: 'ptoName',
      key: 'ptoName',
    },
    {
      title: 'Volume (PTO)',
      dataIndex: 'volume',
      key: 'volume',
    },
    {
      title: 'Earned (MEY)',
      dataIndex: 'earned',
      key: 'earned',
      render: (earned: number) => {
        return <span>{new Intl.NumberFormat('en-US').format(earned || 0)}</span>
      },
    },
  ]

  return (
    <Modal
      open={!!walletAddress}
      centered
      width={700}
      destroyOnClose
      title={
        <Flex gap={12}>
          Referral Performance
          {!!data?.total && (
            <Tag color="blue" style={{ borderRadius: 8 }}>
              {data?.total}
            </Tag>
          )}
        </Flex>
      }
      onCancel={onClose}
      footer={null}
    >
      <Flex
        align="center"
        justify="space-between"
        gap={12}
        style={{ marginBottom: 24 }}
        wrap="wrap"
      >
        <div className="main-description">
          View detailed referral activity and earnings for this partner
        </div>
        <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
          Export
        </Button>
      </Flex>
      <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
        <Typography.Text strong>Filter by PTO:</Typography.Text>
        <SelectPto
          value={ptoId}
          onChange={(value) => handlePtoChange(value as string | undefined)}
          style={{ flex: 1 }}
          placeholder="All PTO"
          pageSize={DEFAULT_PAGE_SIZE_MAX}
        />
      </Flex>
      <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
        <Typography.Text strong>Wallet:</Typography.Text>
        <Input.Search
          placeholder="Search by wallet address"
          allowClear
          onSearch={handleWalletSearch}
          onChange={(e) => {
            if (!e.target.value) {
              handleWalletSearch('')
            }
          }}
          style={{ flex: 1 }}
        />
      </Flex>
      <Table
        columns={columns}
        loading={isLoading}
        dataSource={transactions}
        rowKey={(record, index) => `${record.walletAddress || ''}-${record.ptoName || ''}-${index}`}
        pagination={initPagination(data?.total)}
        onChange={onChangePagination}
        scroll={{ y: 320 }}
        bordered
      />
    </Modal>
  )
}
